import { Offcanvas, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import WebService from "../../utility/WebService";
import logo from "../../assets/default-avatar.jpg";

interface AddSpeakerProps {
  show: boolean;
  handleClose: () => void;
  data?: any; // speaker object from eventSpeakers
  onSuccess?: (speaker: any) => void;
}

type FormData = {
  full_name: string;
  company: string;
  position: string;
};

const AddSpeaker = ({ show, handleClose, data, onSuccess }: AddSpeakerProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setLoading] = useState(false);
  /* ---------------- LOAD DATA FOR EDIT ---------------- */
  useEffect(() => {
    if (data) {
      reset({
        full_name: data.full_name || "",
        position: data.position || "",
        company: data.company || "",
      });
      setPreviewUrl(data.image || null);
    } else {
      reset({
        full_name: "",
        position: "",
        company: "",
      });
      setFile(null);
      setPreviewUrl(null);
    }
  }, [show]);

  /* ---------------- FILE CHANGE ---------------- */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  /* ---------------- SUBMIT (LOCAL ONLY) ---------------- */
  const onSubmit = async (request: FormData) => {
    setLoading(true);

    let imageUrl = data?.image || "";

    // 1️⃣ upload image first
    if (file) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("storage", "EVENTS");

      const res: any = await WebService.fileUploadAPI({
        action: "upload",
        body: fd,
      });

      imageUrl = res.data.url;
    }

    // 2️⃣ return speaker object (NO _id)
    onSuccess?.({
      full_name: request.full_name,
      position: request.position,
      company: request.company,
      image: imageUrl,
    });

    reset();
    setFile(null);
    setPreviewUrl(null);
    handleClose();
    setLoading(false);
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {data ? "Edit Speaker" : "Add Speaker"}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          {/* FULL NAME */}
          <Form.Group className="mb-2">
            <Form.Label className="label">
              Full Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register("full_name", { required: "Full name is required" })}
            />
            {errors.full_name && (
              <div className="error-message">{errors.full_name.message}</div>
            )}
          </Form.Group>

          {/* POSITION */}
          <Form.Group className="mb-2">
            <Form.Label className="label">
              Position
            </Form.Label>
            <Form.Control
              type="text"
              {...register("position")}
            />
            {/* {errors.position && (
              <div className="error-message">{errors.position.message}</div>
            )} */}
          </Form.Group>

          {/* COMPANY */}
          <Form.Group className="mb-2">
            <Form.Label className="label">
              Company
            </Form.Label>
            <Form.Control
              type="text"
              {...register("company")}
            />
            {/* {errors.company && (
              <div className="error-message">{errors.company.message}</div>
            )} */}
          </Form.Group>

          {/* IMAGE */}
          <Form.Group className="mb-2">
            <Form.Label className="label">Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Form.Group>

          {/* PREVIEW */}
          {previewUrl && (
            <div className="mb-2 text-center">
              <img
                src={previewUrl || logo}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: 180,
                  objectFit: "contain",
                }}
              />
            </div>
          )}
        </Form>
      </Offcanvas.Body>

      <div className="p-3">
        <CustomButton
          isLoading={false}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddSpeaker;
